import { mergeLocationLogResultsIntoCache, mergeNcrStatusResultsIntoCache } from "./mdmDeviceCacheUtils";
import { sendDeviceCommandRequest } from "./mdmApi";
import {
  FULLY_SET_BOOLEAN_ENDPOINT,
  FULLY_SETTINGS_ENDPOINTS,
  K10_CSDK_SCHEDULE_ENDPOINTS,
  K10_CSDK_TOGGLE_ENDPOINTS,
  LOAD_APK_FILE_ENDPOINT,
  getActionKeyForEndpoint,
  getTextPayloadKeyForEndpoint,
  isSuccessfulCommandResult,
  normalizeActionKey,
  normalizeDeviceResult,
  parseSseEventBlock,
} from "./mdmCommandUtils";

export {
  FULLY_SET_BOOLEAN_ENDPOINT,
  FULLY_SET_INTEGER_ENDPOINT,
  FULLY_SET_STRING_ENDPOINT,
  FULLY_SETTINGS_ENDPOINTS,
  fullySettingsOptions,
  K10_CSDK_ENDPOINTS,
  K10_CSDK_SCHEDULE_ENDPOINTS,
  K10_CSDK_TOGGLE_ENDPOINTS,
  K10_COMMAND_ENDPOINTS,
  K10_LENOVO_DEVICE_MODEL,
  K10_LENOVO_SHUTDOWN_ENDPOINT,
  LOAD_APK_FILE_ENDPOINT,
} from "./mdmCommandUtils";

const parseRequiredBool = (value, fieldName) => {
  let normalized = value;
  if (typeof normalized === "string") {
    const trimmed = normalized.trim().toLowerCase();
    if (trimmed === "true") normalized = true;
    else if (trimmed === "false") normalized = false;
  }
  if (normalized !== true && normalized !== false) {
    throw new Error(`Payload must include ${fieldName} as true or false.`);
  }
  return normalized;
};

/** Scan & Go Field commands that use streaming (SSE or JSON batch). */
export const FIELD_SCAN_AND_GO_ALLOWED_RELEASE_ENDPOINT =
  "/MDM/ScanAndGo/AllowedReleaseDeviceFromCradle";
export const FIELD_SCAN_AND_GO_RELEASE_DEVICE_ENDPOINT =
  "/MDM/ScanAndGo/ReleaseDeviceFromCradle";
export const FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT = "/MDM/ScanAndGo/ReleaseWallFromCradle";

export const FIELD_SCAN_AND_GO_DEVICE_IDS_ENDPOINTS = new Set([
  FIELD_SCAN_AND_GO_ALLOWED_RELEASE_ENDPOINT,
  FIELD_SCAN_AND_GO_RELEASE_DEVICE_ENDPOINT,
]);

export const FIELD_SCAN_AND_GO_STREAMING_ENDPOINTS = new Set([
  ...FIELD_SCAN_AND_GO_DEVICE_IDS_ENDPOINTS,
  FIELD_SCAN_AND_GO_RELEASE_WALL_ENDPOINT,
]);

export const FIELD_UPDATE_DATABASE_ENDPOINT = "/MDM/Field/UpdateDataBase";
export const FIELD_UPDATE_DATABASE_NO_TIMESTAMP_ENDPOINT = "/MDM/Field/UpdateDataBaseNoTimestamp";
export const FIELD_CLEAR_ALL_AND_UPDATE_ENDPOINT = "/MDM/Field/ClearAllAndUpdate";
export const FIELD_CHANGE_NCR_SERVER_ENDPOINT = "/MDM/Field/ChangeNcrServer";
export const FIELD_FETCH_NCR_STATUS_ENDPOINT = "/MDM/Field/FetchNcrStatus";
export const FIELD_MULTI_SWAN_SONG_ENDPOINT = "/Trigger/SwanSong";
export const FIELD_TRIGGER_CAMERA_ENDPOINT = "/tablet/trigger_camera";
export const FIELD_TRIGGER_SCREENSHOT_ENDPOINT = "/Trigger-Screen-Shot";
export const FIELD_MULTI_FETCH_LOCATION_LOG_ENDPOINT = "/MDM/Field/MultiFetchLocationLog";
export const FIELD_DATABASE_UPDATE_ENDPOINTS = new Set([
  FIELD_UPDATE_DATABASE_ENDPOINT,
  FIELD_UPDATE_DATABASE_NO_TIMESTAMP_ENDPOINT,
  FIELD_CLEAR_ALL_AND_UPDATE_ENDPOINT,
]);
export const FIELD_NCR_SERVER_OPTIONS = [
  { label: "Change to Store Server", value: "MAIN" },
  { label: "Change to Backup Server", value: "BACKUP" },
];

const parseJsonPayload = (payloadText) => {
  try {
    return JSON.parse(payloadText);
  } catch {
    throw new Error("Payload must be valid JSON.");
  }
};

const shouldRefreshDeviceInfoCacheAfterCommand = (endpoint) =>
  endpoint === "/MDM/Fully/DeviceInfo" ||
  endpoint === "/MDM/Fully/DeviceInfo/NoStream" ||
  endpoint === FIELD_SCAN_AND_GO_ALLOWED_RELEASE_ENDPOINT;

export const processMultiLocationLogResults = ({
  payload,
  endpoint,
  upsertDeviceActionResult,
  setCommandSuccessfulDeviceIds,
  setCommandFailedDeviceIds,
  allDevicesRef,
  setAllDevices,
  setCommandUpdatedDevices,
}) => {
  const actionKey = getActionKeyForEndpoint(endpoint);
  let success = 0;
  let failed = 0;
  const successfulResults = [];

  const processOne = (candidate) => {
    const normalized = normalizeDeviceResult(candidate);
    const normalizedData = normalized.normalizedResult;
    const deviceId = String(normalized.deviceId || "").trim();
    if (!deviceId) return;

    const isSuccess = isSuccessfulCommandResult(normalizedData);
    if (isSuccess) {
      success += 1;
      successfulResults.push(normalizedData ?? candidate);
      setCommandSuccessfulDeviceIds?.((prev) =>
        prev.includes(deviceId) ? prev : [...prev, deviceId]
      );
    } else {
      failed += 1;
      setCommandFailedDeviceIds?.((prev) =>
        prev.includes(deviceId) ? prev : [...prev, deviceId]
      );
    }

    upsertDeviceActionResult?.(deviceId, actionKey, {
      endpoint,
      action: actionKey,
      status: normalizedData?.status || (isSuccess ? "ok" : "failed"),
      data: normalizedData?.data ?? normalizedData ?? candidate,
      raw_result: normalizedData ?? candidate,
      updated_at:
        normalizedData?.updated_at ||
        normalizedData?.data?.updated_at ||
        new Date().toISOString(),
    });
  };

  const finalize = () => {
    if (successfulResults.length && allDevicesRef?.current) {
      const merged = mergeLocationLogResultsIntoCache(allDevicesRef.current, successfulResults);
      if (merged.updatedCount > 0) {
        allDevicesRef.current = merged.nextDevices;
        setAllDevices?.(merged.nextDevices);
        setCommandUpdatedDevices?.((prev) => prev + merged.updatedCount);
      }
    }
    return { success, failed };
  };

  if (Array.isArray(payload)) {
    payload.forEach(processOne);
    return finalize();
  }

  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.results)) {
      payload.results.forEach(processOne);
      return finalize();
    }
    if (payload.devices && typeof payload.devices === "object") {
      Object.entries(payload.devices).forEach(([deviceId, value]) => {
        processOne({ [deviceId]: value });
      });
      return finalize();
    }
    processOne(payload);
  }

  return finalize();
};

export const processMultiNcrStatusResults = ({
  payload,
  endpoint,
  upsertDeviceActionResult,
  setCommandSuccessfulDeviceIds,
  setCommandFailedDeviceIds,
  allDevicesRef,
  setAllDevices,
  setCommandUpdatedDevices,
}) => {
  const actionKey = getActionKeyForEndpoint(endpoint);
  let success = 0;
  let failed = 0;
  const successfulResults = [];

  const processOne = (candidate) => {
    const normalized = normalizeDeviceResult(candidate);
    const normalizedData = normalized.normalizedResult;
    const deviceId = String(normalized.deviceId || "").trim();
    if (!deviceId) return;

    const isSuccess = isSuccessfulCommandResult(normalizedData);
    if (isSuccess) {
      success += 1;
      successfulResults.push(normalizedData ?? candidate);
      setCommandSuccessfulDeviceIds?.((prev) =>
        prev.includes(deviceId) ? prev : [...prev, deviceId]
      );
    } else {
      failed += 1;
      setCommandFailedDeviceIds?.((prev) =>
        prev.includes(deviceId) ? prev : [...prev, deviceId]
      );
    }

    upsertDeviceActionResult?.(deviceId, actionKey, {
      endpoint,
      action: actionKey,
      status: normalizedData?.status || (isSuccess ? "ok" : "failed"),
      data: normalizedData?.data ?? normalizedData ?? candidate,
      raw_result: normalizedData ?? candidate,
      updated_at:
        normalizedData?.app_timestamp ||
        normalizedData?.data?.app_timestamp ||
        new Date().toISOString(),
    });
  };

  const finalize = () => {
    if (successfulResults.length && allDevicesRef?.current) {
      const merged = mergeNcrStatusResultsIntoCache(allDevicesRef.current, successfulResults);
      if (merged.updatedCount > 0) {
        allDevicesRef.current = merged.nextDevices;
        setAllDevices?.(merged.nextDevices);
        setCommandUpdatedDevices?.((prev) => prev + merged.updatedCount);
      }
    }
    return { success, failed };
  };

  if (Array.isArray(payload)) {
    payload.forEach(processOne);
    return finalize();
  }

  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.results)) {
      payload.results.forEach(processOne);
      return finalize();
    }
  }

  return { success, failed };
};

const withTextPayload = (payload, endpoint, options, messageText) => {
  const textPayloadKey = getTextPayloadKeyForEndpoint(endpoint, options);
  if (!textPayloadKey) return payload;

  const message = String(messageText || "").trim();
  if (!message) throw new Error("Please enter text for this command.");
  return { ...payload, [textPayloadKey]: message };
};

const applyDeviceInfoMerge = ({
  normalizedData,
  allDevicesRef,
  mergeDeviceResultsIntoCache,
  setAllDevices,
  setCommandUpdatedDevices,
}) => {
  if (normalizedData?.status !== "ok") return;
  const merged = mergeDeviceResultsIntoCache(allDevicesRef.current, [normalizedData]);
  if (merged.updatedCount <= 0) return;
  allDevicesRef.current = merged.nextDevices;
  setAllDevices(merged.nextDevices);
  setCommandUpdatedDevices((prev) => prev + merged.updatedCount);
};

const handleStreamResponse = async ({
  response,
  endpoint,
  actionKey,
  token,
  upsertDeviceActionResult,
  allDevicesRef,
  mergeDeviceResultsIntoCache,
  setAllDevices,
  setCommandUpdatedDevices,
  setCommandProgress,
  setCommandSuccessfulDeviceIds,
  setCommandFailedDeviceIds,
  fetchDeviceInfoCache,
}) => {
  if (!response.body) throw new Error("Failed to read response stream.");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() || "";

    chunks.forEach((rawBlock) => {
      const parsedEvent = parseSseEventBlock(rawBlock);
      if (!parsedEvent) return;

      const { event, data } = parsedEvent;
      if (event === "started" && typeof data === "object" && data !== null) {
        setCommandProgress((prev) => ({
          ...prev,
          total: Number.isFinite(Number(data.total)) ? Number(data.total) : prev.total,
          status: "running",
        }));
        return;
      }

      if (event === "device_result" && typeof data === "object" && data !== null) {
        const normalized = normalizeDeviceResult(data);
        const normalizedData = normalized.normalizedResult;
        const isSuccess = isSuccessfulCommandResult(normalizedData);
        const resolvedActionKey = normalizeActionKey(normalizedData?.action) || actionKey;

        if (normalized.deviceId) {
          if (isSuccess) {
            setCommandSuccessfulDeviceIds((prev) =>
              prev.includes(normalized.deviceId) ? prev : [...prev, normalized.deviceId]
            );
          } else {
            setCommandFailedDeviceIds((prev) =>
              prev.includes(normalized.deviceId) ? prev : [...prev, normalized.deviceId]
            );
          }

          upsertDeviceActionResult(normalized.deviceId, resolvedActionKey, {
            endpoint,
            action: resolvedActionKey,
            status: normalizedData?.status || (isSuccess ? "ok" : "failed"),
            http_status: normalizedData?.http_status,
            content_type: normalizedData?.content_type,
            data: normalizedData?.data ?? null,
            raw_result: normalizedData,
            updated_at:
              normalizedData?.updated_at ||
              normalizedData?.data?.updated_at ||
              new Date().toISOString(),
          });
        }

        if (endpoint === "/MDM/Fully/DeviceInfo") {
          applyDeviceInfoMerge({
            normalizedData,
            allDevicesRef,
            mergeDeviceResultsIntoCache,
            setAllDevices,
            setCommandUpdatedDevices,
          });
        }
        return;
      }

      if (event === "progress" && typeof data === "object" && data !== null) {
        setCommandProgress((prev) => ({
          ...prev,
          done: Number.isFinite(Number(data.done)) ? Number(data.done) : prev.done,
          total: Number.isFinite(Number(data.total)) ? Number(data.total) : prev.total,
          success: Number.isFinite(Number(data.success)) ? Number(data.success) : prev.success,
          failed: Number.isFinite(Number(data.failed)) ? Number(data.failed) : prev.failed,
          status: "running",
        }));
        return;
      }

      if (event === "completed" && typeof data === "object" && data !== null) {
        setCommandProgress((prev) => ({
          ...prev,
          done: Number.isFinite(Number(data.total)) ? Number(data.total) : prev.done,
          total: Number.isFinite(Number(data.total)) ? Number(data.total) : prev.total,
          success: Number.isFinite(Number(data.success)) ? Number(data.success) : prev.success,
          failed: Number.isFinite(Number(data.failed)) ? Number(data.failed) : prev.failed,
          status: "completed",
        }));
        if (shouldRefreshDeviceInfoCacheAfterCommand(endpoint)) {
          fetchDeviceInfoCache(token).catch((err) => console.error(err));
        }
      }
    });
  }

  const parsedRemainder = parseSseEventBlock(buffer);
  if (
    endpoint === "/MDM/Fully/DeviceInfo" &&
    parsedRemainder &&
    parsedRemainder.event === "device_result" &&
    typeof parsedRemainder.data === "object"
  ) {
    const normalized = normalizeDeviceResult(parsedRemainder.data).normalizedResult;
    applyDeviceInfoMerge({
      normalizedData: normalized,
      allDevicesRef,
      mergeDeviceResultsIntoCache,
      setAllDevices,
      setCommandUpdatedDevices,
    });
  }
};

const handleNonStreamResponse = async ({
  response,
  payloadTargetCount,
  endpoint,
  token,
  setCommandProgress,
  fetchDeviceInfoCache,
}) => {
  let nonStreamResult = null;
  try {
    nonStreamResult = await response.json();
  } catch {
    nonStreamResult = null;
  }
  const reportedSuccess = Number(nonStreamResult?.success_count);
  const reportedFailed = Number(nonStreamResult?.failure_count);
  const hasReportedSuccess = Number.isFinite(reportedSuccess);
  const hasReportedFailed = Number.isFinite(reportedFailed);
  const nextSuccess = hasReportedSuccess ? reportedSuccess : 0;
  const nextFailed = hasReportedFailed ? reportedFailed : 0;
  const nextTotal =
    hasReportedSuccess || hasReportedFailed ? nextSuccess + nextFailed : payloadTargetCount;

  setCommandProgress((prev) => ({
    ...prev,
    done: nextTotal,
    total: nextTotal,
    success: nextSuccess,
    failed: nextFailed,
    status: "completed",
  }));

  if (shouldRefreshDeviceInfoCacheAfterCommand(endpoint)) {
    fetchDeviceInfoCache(token).catch((err) => console.error(err));
  }
};

const runStreamingCommand = async ({
  serverUrl,
  endpoint,
  token,
  payload,
  payloadTargetCount,
  throwIfRequestFailed,
  setCommandProgress,
  ...rest
}) => {
  const response = await sendDeviceCommandRequest(serverUrl, endpoint, token, payload);
  await throwIfRequestFailed(response, "Failed to send command");

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("text/event-stream")) {
    await handleStreamResponse({ response, endpoint, ...rest, token, setCommandProgress });
    return;
  }

  await handleNonStreamResponse({
    response,
    payloadTargetCount,
    endpoint,
    token,
    setCommandProgress,
    fetchDeviceInfoCache: rest.fetchDeviceInfoCache,
  });
};

export const runFieldCommand = async ({
  serverUrl,
  endpoint,
  commandPayloadText,
  commandMessageText,
  textCommandOptions,
  isBranchLevelFullyView,
  isSingleFullyBranchSelection,
  fieldTriggerConfigUpdateEndpoint,
  fieldTriggerMicromomentEndpoint,
  fieldScanAndGoReleaseWallEndpoint,
  fieldMultiSwanSongEndpoint,
  fieldTriggerCameraEndpoint,
  fieldTriggerScreenshotEndpoint,
  fieldMultiFetchLocationLogEndpoint,
  fieldFetchNcrStatusEndpoint,
  isScanAndGoReleaseWallAllowed,
  getToken,
  throwIfRequestFailed,
  setCommandProgress,
  setFieldCommandResponse,
  ...deviceStreamRest
}) => {
  let payload = parseJsonPayload(commandPayloadText);
  payload = withTextPayload(payload, endpoint, textCommandOptions, commandMessageText);

  const isMicromoment = endpoint === fieldTriggerMicromomentEndpoint;
  if (isMicromoment) {
    if (!isBranchLevelFullyView) {
      throw new Error("Trigger Micromoment is available only on branch level.");
    }
    if (!isSingleFullyBranchSelection) {
      throw new Error("All selected devices must be in the same branch and retailer.");
    }
  }

  const token = getToken();
  if (endpoint === FIELD_CHANGE_NCR_SERVER_ENDPOINT) {
    if (!isBranchLevelFullyView) {
      throw new Error("Change NCR Server is available only on branch level.");
    }
    const payloadDeviceIds = Array.isArray(payload?.device_ids)
      ? payload.device_ids.map((id) => String(id).trim()).filter(Boolean)
      : [];
    const retailerId = Number(payload?.retailer_id);
    const branchId = Number(payload?.branch_id);
    const notificationMessage = String(payload?.notification_message || "")
      .trim()
      .toUpperCase();
    if (!payloadDeviceIds.length) {
      throw new Error("Payload must include at least one device in device_ids array.");
    }
    if (!Number.isFinite(retailerId) || !Number.isFinite(branchId)) {
      throw new Error("Payload must include numeric retailer_id and branch_id.");
    }
    if (notificationMessage !== "MAIN" && notificationMessage !== "BACKUP") {
      throw new Error("Payload must include notification_message as MAIN or BACKUP.");
    }
    const ncrPayload = {
      ...payload,
      device_ids: payloadDeviceIds,
      retailer_id: retailerId,
      branch_id: branchId,
      notification_message: notificationMessage,
    };

    setCommandProgress({
      total: payloadDeviceIds.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(
      serverUrl,
      endpoint,
      token,
      ncrPayload
    );
    await throwIfRequestFailed(triggerResponse, "Failed to change NCR server");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);
    const successCount = Number(triggerPayload?.success_count);
    const failureCount = Number(triggerPayload?.failure_count);
    const resolvedSuccess = Number.isFinite(successCount) ? successCount : 0;
    const resolvedFailure = Number.isFinite(failureCount) ? failureCount : 0;
    const resolvedTotal = resolvedSuccess + resolvedFailure;
    setCommandProgress({
      total: resolvedTotal || payloadDeviceIds.length,
      done: resolvedTotal || payloadDeviceIds.length,
      success: resolvedSuccess,
      failed: resolvedFailure,
      status: "completed",
    });
    return;
  }
  if (FIELD_DATABASE_UPDATE_ENDPOINTS.has(endpoint)) {
    if (!isBranchLevelFullyView) {
      throw new Error("Trigger Database Update is available only on branch level.");
    }
    const payloadDeviceIds = Array.isArray(payload?.device_ids)
      ? payload.device_ids.map((id) => String(id).trim()).filter(Boolean)
      : [];
    const retailerId = Number(payload?.retailer_id);
    const branchId = Number(payload?.branch_id);
    if (!payloadDeviceIds.length) {
      throw new Error("Payload must include at least one device in device_ids array.");
    }
    if (!Number.isFinite(retailerId) || !Number.isFinite(branchId)) {
      throw new Error("Payload must include numeric retailer_id and branch_id.");
    }
    const databasePayload = {
      ...payload,
      device_ids: payloadDeviceIds,
      retailer_id: retailerId,
      branch_id: branchId,
    };

    setCommandProgress({
      total: payloadDeviceIds.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(
      serverUrl,
      endpoint,
      token,
      databasePayload
    );
    await throwIfRequestFailed(triggerResponse, "Failed to trigger database update");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);
    const successCount = Number(triggerPayload?.success_count);
    const failureCount = Number(triggerPayload?.failure_count);
    const resolvedSuccess = Number.isFinite(successCount) ? successCount : 0;
    const resolvedFailure = Number.isFinite(failureCount) ? failureCount : 0;
    const resolvedTotal = resolvedSuccess + resolvedFailure;
    setCommandProgress({
      total: resolvedTotal || payloadDeviceIds.length,
      done: resolvedTotal || payloadDeviceIds.length,
      success: resolvedSuccess,
      failed: resolvedFailure,
      status: "completed",
    });
    return;
  }

  if (endpoint === fieldTriggerConfigUpdateEndpoint) {
    const payloadDeviceIds = payload?.devices;
    if (!Array.isArray(payloadDeviceIds) || payloadDeviceIds.length === 0) {
      throw new Error("Payload must include at least one device in devices array.");
    }
    const configCategory = String(payload?.config_category || "").trim();
    const configValue = String(payload?.config_value || "").trim();
    if (!configCategory) throw new Error("Payload must include config_category.");
    if (!configValue) throw new Error("Payload must include config_value.");

    setCommandProgress({
      total: payloadDeviceIds.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(serverUrl, endpoint, token, payload);
    await throwIfRequestFailed(triggerResponse, "Failed to trigger config update");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);
    const successCount = Number(triggerPayload?.success_count);
    const failureCount = Number(triggerPayload?.failure_count);
    const resolvedSuccess = Number.isFinite(successCount) ? successCount : 0;
    const resolvedFailure = Number.isFinite(failureCount) ? failureCount : 0;
    const resolvedTotal = resolvedSuccess + resolvedFailure;
    setCommandProgress({
      total: resolvedTotal || payloadDeviceIds.length,
      done: resolvedTotal || payloadDeviceIds.length,
      success: resolvedSuccess,
      failed: resolvedFailure,
      status: "completed",
    });
    return;
  }

  if (isMicromoment) {
    const payloadDeviceIds = Array.isArray(payload?.device_ids)
      ? payload.device_ids.map((id) => String(id).trim()).filter(Boolean)
      : [];
    const micromommentId = String(payload?.micromomment_id || "").trim();
    const retailer = Number(payload?.retailer);
    const store = Number(payload?.store);
    if (!payloadDeviceIds.length) {
      throw new Error("Payload must include at least one device in device_ids array.");
    }
    if (!micromommentId) throw new Error("Payload must include micromomment_id.");
    if (!Number.isFinite(retailer) || !Number.isFinite(store)) {
      throw new Error("Payload must include numeric retailer and store.");
    }
    payload = {
      ...payload,
      device_ids: payloadDeviceIds,
      micromomment_id: micromommentId,
      retailer,
      store,
    };

    setCommandProgress({
      total: payloadDeviceIds.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(serverUrl, endpoint, token, payload);
    await throwIfRequestFailed(triggerResponse, "Failed to trigger micromoment");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);
    setCommandProgress({
      total: payloadDeviceIds.length,
      done: payloadDeviceIds.length,
      success: payloadDeviceIds.length,
      failed: 0,
      status: "completed",
    });
    return;
  }

  if (endpoint === fieldScanAndGoReleaseWallEndpoint) {
    if (!isScanAndGoReleaseWallAllowed) {
      throw new Error(
        "Release Wall From Cradle is only available on a single branch or a folder within a branch (not retailer-wide)."
      );
    }
    const retailerId = Number(payload?.retailer_id);
    const branchId = Number(payload?.branch_id);
    const rawWallIds = payload?.wall_id;
    const wallList = Array.isArray(rawWallIds)
      ? rawWallIds.map((w) => String(w).trim()).filter(Boolean)
      : rawWallIds !== null && rawWallIds !== undefined && String(rawWallIds).trim() !== ""
        ? [String(rawWallIds).trim()]
        : [];
    if (!Number.isFinite(retailerId) || !Number.isFinite(branchId)) {
      throw new Error("Payload must include numeric retailer_id and branch_id.");
    }
    if (!wallList.length) {
      throw new Error("Payload must include a non-empty wall_id list.");
    }
    const wallPayload = {
      ...payload,
      retailer_id: retailerId,
      branch_id: branchId,
      wall_id: wallList,
    };
    const payloadTargetCount = wallList.length;

    setCommandProgress({
      total: payloadTargetCount,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    await runStreamingCommand({
      serverUrl,
      endpoint,
      token,
      payload: wallPayload,
      payloadTargetCount,
      throwIfRequestFailed,
      setCommandProgress,
      actionKey: getActionKeyForEndpoint(endpoint),
      ...deviceStreamRest,
    });
    return;
  }

  if (FIELD_SCAN_AND_GO_DEVICE_IDS_ENDPOINTS.has(endpoint)) {
    const rawDeviceIds = payload?.device_ids;
    if (!Array.isArray(rawDeviceIds) || rawDeviceIds.length === 0) {
      throw new Error("Payload must include at least one device in device_ids array.");
    }
    const payloadDeviceIds = rawDeviceIds.map((id) => String(id).trim()).filter(Boolean);
    if (!payloadDeviceIds.length) {
      throw new Error("Payload must include at least one device in device_ids array.");
    }
    let streamPayload = { ...payload, device_ids: payloadDeviceIds };
    if (endpoint === FIELD_SCAN_AND_GO_ALLOWED_RELEASE_ENDPOINT) {
      let allowed = payload?.allowed;
      if (typeof allowed === "string") {
        const t = allowed.trim().toLowerCase();
        if (t === "true") allowed = true;
        else if (t === "false") allowed = false;
      }
      if (allowed !== true && allowed !== false) {
        throw new Error("Payload must include allowed as true or false.");
      }
      let notes;
      if (payload?.notes != null) {
        if (typeof payload.notes !== "string") {
          throw new Error("Payload notes must be a string.");
        }
        notes = payload.notes.trim();
      }
      if (allowed === false && !notes) {
        throw new Error("Notes are required when setting Allowed to False.");
      }
      streamPayload = { ...streamPayload, allowed };
      if (notes) {
        streamPayload.notes = notes;
      }
    }
    const payloadTargetCount = payloadDeviceIds.length;

    setCommandProgress({
      total: payloadTargetCount,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    await runStreamingCommand({
      serverUrl,
      endpoint,
      token,
      payload: streamPayload,
      payloadTargetCount,
      throwIfRequestFailed,
      setCommandProgress,
      actionKey: getActionKeyForEndpoint(endpoint),
      ...deviceStreamRest,
    });
    return;
  }

  if (endpoint === fieldMultiFetchLocationLogEndpoint) {
    if (!isBranchLevelFullyView) {
      throw new Error("Get Location/Version is available only on branch level.");
    }
    const payloadDeviceIds = Array.isArray(payload?.device_ids)
      ? payload.device_ids.map((id) => String(id).trim()).filter(Boolean)
      : [];
    const retailerId = Number(payload?.retailer_id);
    const branchId = Number(payload?.branch_id);
    if (!payloadDeviceIds.length) {
      throw new Error("Select at least one device before running Get Location/Version.");
    }
    if (!Number.isFinite(retailerId) || !Number.isFinite(branchId)) {
      throw new Error("Payload must include numeric retailer_id and branch_id.");
    }
    const locationPayload = {
      ...payload,
      device_ids: payloadDeviceIds,
      retailer_id: retailerId,
      branch_id: branchId,
    };

    setCommandProgress({
      total: payloadDeviceIds.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(
      serverUrl,
      endpoint,
      token,
      locationPayload
    );
    await throwIfRequestFailed(triggerResponse, "Failed to fetch location/version");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);

    const processedCounts = processMultiLocationLogResults({
      payload: triggerPayload,
      endpoint,
      upsertDeviceActionResult: deviceStreamRest.upsertDeviceActionResult,
      setCommandSuccessfulDeviceIds: deviceStreamRest.setCommandSuccessfulDeviceIds,
      setCommandFailedDeviceIds: deviceStreamRest.setCommandFailedDeviceIds,
      allDevicesRef: deviceStreamRest.allDevicesRef,
      setAllDevices: deviceStreamRest.setAllDevices,
      setCommandUpdatedDevices: deviceStreamRest.setCommandUpdatedDevices,
    });

    const successCount = Number(triggerPayload?.success_count);
    const failureCount = Number(triggerPayload?.failure_count);
    const hasReportedSuccess = Number.isFinite(successCount);
    const hasReportedFailed = Number.isFinite(failureCount);
    const resolvedSuccess = hasReportedSuccess ? successCount : processedCounts.success;
    const resolvedFailure = hasReportedFailed ? failureCount : processedCounts.failed;
    const resolvedTotal = hasReportedSuccess || hasReportedFailed
      ? resolvedSuccess + resolvedFailure
      : payloadDeviceIds.length;

    setCommandProgress({
      total: resolvedTotal || payloadDeviceIds.length,
      done: resolvedTotal || payloadDeviceIds.length,
      success: resolvedSuccess,
      failed: resolvedFailure,
      status: "completed",
    });
    return;
  }

  if (endpoint === fieldFetchNcrStatusEndpoint) {
    if (!isBranchLevelFullyView) {
      throw new Error("Fetch NCR Status is available only on branch level.");
    }
    const payloadDeviceIds = Array.isArray(payload?.device_ids)
      ? payload.device_ids.map((id) => String(id).trim()).filter(Boolean)
      : [];
    const retailerId = Number(payload?.retailer_id);
    const branchId = Number(payload?.branch_id);
    if (!payloadDeviceIds.length) {
      throw new Error("Select at least one device before running Fetch NCR Status.");
    }
    if (!Number.isFinite(retailerId) || !Number.isFinite(branchId)) {
      throw new Error("Payload must include numeric retailer_id and branch_id.");
    }
    const ncrStatusPayload = {
      ...payload,
      device_ids: payloadDeviceIds,
      retailer_id: retailerId,
      branch_id: branchId,
    };

    setCommandProgress({
      total: payloadDeviceIds.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(
      serverUrl,
      endpoint,
      token,
      ncrStatusPayload
    );
    await throwIfRequestFailed(triggerResponse, "Failed to fetch NCR status");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);

    const processedCounts = processMultiNcrStatusResults({
      payload: triggerPayload,
      endpoint,
      upsertDeviceActionResult: deviceStreamRest.upsertDeviceActionResult,
      setCommandSuccessfulDeviceIds: deviceStreamRest.setCommandSuccessfulDeviceIds,
      setCommandFailedDeviceIds: deviceStreamRest.setCommandFailedDeviceIds,
      allDevicesRef: deviceStreamRest.allDevicesRef,
      setAllDevices: deviceStreamRest.setAllDevices,
      setCommandUpdatedDevices: deviceStreamRest.setCommandUpdatedDevices,
    });

    const successCount = Number(triggerPayload?.success_count);
    const failureCount = Number(triggerPayload?.failure_count);
    const hasReportedSuccess = Number.isFinite(successCount);
    const hasReportedFailed = Number.isFinite(failureCount);
    const resolvedSuccess = hasReportedSuccess ? successCount : processedCounts.success;
    const resolvedFailure = hasReportedFailed ? failureCount : processedCounts.failed;
    const resolvedTotal = hasReportedSuccess || hasReportedFailed
      ? resolvedSuccess + resolvedFailure
      : payloadDeviceIds.length;

    setCommandProgress({
      total: resolvedTotal || payloadDeviceIds.length,
      done: resolvedTotal || payloadDeviceIds.length,
      success: resolvedSuccess,
      failed: resolvedFailure,
      status: "completed",
    });
    return;
  }

  if (endpoint === fieldMultiSwanSongEndpoint) {
    const rawTablets = payload?.tablets;
    if (!Array.isArray(rawTablets) || rawTablets.length === 0) {
      throw new Error("Payload must include at least one device in tablets array.");
    }
    const tablets = rawTablets.map((id) => String(id).trim()).filter(Boolean);
    if (!tablets.length) {
      throw new Error("Payload must include at least one device in tablets array.");
    }
    const message = String(payload?.message || "").trim() || "Trigger swan song";
    const swanPayload = { tablets, message };

    setCommandProgress({
      total: tablets.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(
      serverUrl,
      endpoint,
      token,
      swanPayload
    );
    await throwIfRequestFailed(triggerResponse, "Failed to trigger swan song");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);
    setCommandProgress({
      total: tablets.length,
      done: tablets.length,
      success: tablets.length,
      failed: 0,
      status: "completed",
    });
    return;
  }

  if (endpoint === fieldTriggerCameraEndpoint) {
    if (!isBranchLevelFullyView) {
      throw new Error("Camera is available only on branch level.");
    }
    const rawSerialNumbers = payload?.serial_numbers;
    if (!Array.isArray(rawSerialNumbers) || rawSerialNumbers.length === 0) {
      throw new Error("Payload must include at least one device in serial_numbers array.");
    }
    const serialNumbers = rawSerialNumbers.map((id) => String(id).trim()).filter(Boolean);
    if (!serialNumbers.length) {
      throw new Error("Payload must include at least one device in serial_numbers array.");
    }
    const retailer = payload?.retailer;
    const store = payload?.store;
    if (retailer == null || store == null || retailer === "" || store === "") {
      throw new Error("Payload must include retailer and store.");
    }
    const cameraPayload = {
      serial_numbers: serialNumbers,
      retailer,
      store,
    };

    setCommandProgress({
      total: serialNumbers.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(
      serverUrl,
      endpoint,
      token,
      cameraPayload
    );
    await throwIfRequestFailed(triggerResponse, "Failed to trigger camera");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);
    setCommandProgress({
      total: serialNumbers.length,
      done: serialNumbers.length,
      success: serialNumbers.length,
      failed: 0,
      status: "completed",
    });
    return triggerPayload;
  }

  if (endpoint === fieldTriggerScreenshotEndpoint) {
    if (!isBranchLevelFullyView) {
      throw new Error("Screenshot is available only on branch level.");
    }
    const rawSerialNumbers = payload?.serial_numbers;
    if (!Array.isArray(rawSerialNumbers) || rawSerialNumbers.length === 0) {
      throw new Error("Payload must include at least one device in serial_numbers array.");
    }
    const serialNumbers = rawSerialNumbers.map((id) => String(id).trim()).filter(Boolean);
    if (!serialNumbers.length) {
      throw new Error("Payload must include at least one device in serial_numbers array.");
    }
    const retailer = payload?.retailer;
    const store = payload?.store;
    if (retailer == null || store == null || retailer === "" || store === "") {
      throw new Error("Payload must include retailer and store.");
    }
    const screenshotPayload = {
      serial_numbers: serialNumbers,
      retailer,
      store,
    };

    setCommandProgress({
      total: serialNumbers.length,
      done: 0,
      success: 0,
      failed: 0,
      status: "running",
    });

    const triggerResponse = await sendDeviceCommandRequest(
      serverUrl,
      endpoint,
      token,
      screenshotPayload
    );
    await throwIfRequestFailed(triggerResponse, "Failed to trigger screenshot");
    const triggerPayload = await triggerResponse.json();
    setFieldCommandResponse(triggerPayload);
    setCommandProgress({
      total: serialNumbers.length,
      done: serialNumbers.length,
      success: serialNumbers.length,
      failed: 0,
      status: "completed",
    });
    return triggerPayload;
  }

  throw new Error("Unsupported Field command endpoint.");
};

export const runMdmCommand = async ({
  serverUrl,
  endpoint,
  commandPayloadText,
  commandMessageText,
  textCommandOptions,
  getToken,
  throwIfRequestFailed,
  setCommandProgress,
  ...rest
}) => {
  let payload = parseJsonPayload(commandPayloadText);
  payload = withTextPayload(payload, endpoint, textCommandOptions, commandMessageText);

  if (FULLY_SETTINGS_ENDPOINTS.has(endpoint)) {
    const key = String(payload?.key ?? "").trim();
    if (!key) throw new Error("Payload must include key.");

    if (endpoint === FULLY_SET_BOOLEAN_ENDPOINT) {
      let boolValue = payload?.value;
      if (typeof boolValue === "string") {
        const normalized = boolValue.trim().toLowerCase();
        if (normalized === "true") boolValue = true;
        else if (normalized === "false") boolValue = false;
      }
      if (boolValue !== true && boolValue !== false) {
        throw new Error("Payload must include value as true or false.");
      }
      payload = { ...payload, key, value: boolValue };
    } else if (String(payload?.value ?? "").trim() === "") {
      throw new Error("Payload must include value.");
    } else {
      payload = { ...payload, key, value: String(payload.value).trim() };
    }
  }

  if (endpoint === LOAD_APK_FILE_ENDPOINT) {
    const url = String(payload?.url ?? "").trim();
    if (!url) throw new Error("Please enter a URL for Load APK File.");
    payload = { ...payload, url };
  }

  if (K10_CSDK_TOGGLE_ENDPOINTS.has(endpoint)) {
    payload = {
      ...payload,
      enabled: parseRequiredBool(payload?.enabled, "enabled"),
      on_start_app_enabled: parseRequiredBool(payload?.on_start_app_enabled, "on_start_app_enabled"),
    };
  }

  if (K10_CSDK_SCHEDULE_ENDPOINTS.has(endpoint)) {
    const hour = Number(payload?.hour);
    const minute = Number(payload?.minute);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
      throw new Error("Payload must include hour as an integer from 0 to 23.");
    }
    if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
      throw new Error("Payload must include minute as an integer from 0 to 59.");
    }
    payload = {
      ...payload,
      hour,
      minute,
      enabled: parseRequiredBool(payload?.enabled, "enabled"),
      on_start_app_enabled: parseRequiredBool(payload?.on_start_app_enabled, "on_start_app_enabled"),
    };
  }

  const payloadDeviceIds = payload?.devices;
  if (!Array.isArray(payloadDeviceIds) || payloadDeviceIds.length === 0) {
    throw new Error("Payload must include at least one device in devices array.");
  }
  const payloadTargetCount = payloadDeviceIds.length;

  setCommandProgress({
    total: payloadTargetCount,
    done: 0,
    success: 0,
    failed: 0,
    status: "running",
  });

  const token = getToken();
  await runStreamingCommand({
    serverUrl,
    endpoint,
    token,
    payload,
    payloadTargetCount,
    throwIfRequestFailed,
    setCommandProgress,
    actionKey: getActionKeyForEndpoint(endpoint),
    ...rest,
  });
};
